import { type TrackingLabelProviderGroup } from '@cart-services/utils/official-website-order-shipments'
import { useDialog } from '@dni-hooks/useDialog'
import { Dialog } from '@pets-ui/Dialog/Dialog'
import { DialogContent } from '@pets-ui/DialogContent/DialogContent'
import { DialogHeader } from '@pets-ui/DialogHeader/DialogHeader'
import { Table } from '@pets-ui/Table/Table'
import { TableBody } from '@pets-ui/TableBody/TableBody'
import { TableCell } from '@pets-ui/TableCell/TableCell'
import { TableHead } from '@pets-ui/TableHead/TableHead'
import { TableRow } from '@pets-ui/TableRow/TableRow'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { VStack } from '@pets-ui/VStack/VStack'
import * as stylex from '@stylexjs/stylex'
import { useTranslation } from 'react-i18next'
import { ShipmentTrackingNumber } from './ShipmentTrackingNumber'

export const DIALOG_TRACKING_LABEL_STATUS_KEY = 'DialogTrackingLabelStatus'

export interface DialogTrackingLabelStatusExtraProps {
  trackingLabelGroup: TrackingLabelProviderGroup
}

const styles = stylex.create({
  table: {
    borderCollapse: 'collapse',
    tableLayout: 'fixed',
    width: '100%',
  },
  tableHead: {
    backgroundColor: colors.grey100,
  },
  cell: {
    padding: '8px',
    borderBottomColor: colors.grey300,
    borderBottomStyle: 'solid',
    borderBottomWidth: '1px',
    borderLeftWidth: 0,
    borderRightColor: colors.grey300,
    borderRightStyle: 'solid',
    borderRightWidth: '1px',
    borderTopColor: colors.grey300,
    borderTopStyle: 'solid',
    borderTopWidth: '1px',
  },
  cellFirst: {
    borderLeftColor: colors.grey300,
    borderLeftStyle: 'solid',
    borderLeftWidth: '1px',
  },
  headerCell: {
    textTransform: 'none',
    height: '44px',
  },
  headerCellStatus: {
    width: '160px',
  },
})

export function DialogTrackingLabelStatus() {
  const { t } = useTranslation()
  const { open, closeDialog, trackingLabelGroup } = useDialog<DialogTrackingLabelStatusExtraProps>(DIALOG_TRACKING_LABEL_STATUS_KEY)
  const providerName = trackingLabelGroup?.provider_name ?? ''
  const statusGroups = trackingLabelGroup?.statusGroups ?? []
  const hasTrackingStatus = statusGroups.some(group => group.tracking_status !== null)

  return (
    <Dialog open={open} onClose={closeDialog}>
      <DialogHeader title={hasTrackingStatus ? t('orderDetail.trackingStatus', { defaultValue: '託運狀態' }) : t('orderDetail.parcelStatus', { defaultValue: '包裹狀態' })} onClose={closeDialog} />
      <DialogContent>
        <VStack gap={3}>
          <Text variant="subtitle">
            {t('orderDetail.logisticsProvider', { defaultValue: '物流' }) }
            :
            {' '}
            {providerName}
          </Text>
          <Table rootStyle={styles.table}>
            <TableHead rootStyle={styles.tableHead}>
              <TableRow>
                {hasTrackingStatus && (
                  <TableCell rootStyle={[styles.cell, styles.cellFirst, styles.headerCell, styles.headerCellStatus]}>
                    <Text variant="body" fontWeight="bold">{t('orderDetail.trackingStatus', { defaultValue: '託運狀態' })}</Text>
                  </TableCell>
                )}
                <TableCell rootStyle={[styles.cell, !hasTrackingStatus && styles.cellFirst, styles.headerCell]}>
                  <Text variant="body" fontWeight="bold">{t('orderDetail.trackingNumber', { defaultValue: '託運單號' })}</Text>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {statusGroups.flatMap(({ tracking_status_name, trackingLabels }) =>
                trackingLabels.map(trackingLabel => (
                  <TableRow key={trackingLabel.tracking_no}>
                    {hasTrackingStatus && (
                      <TableCell rootStyle={[styles.cell, styles.cellFirst]}>
                        <Text>{tracking_status_name ?? '-'}</Text>
                      </TableCell>
                    )}
                    <TableCell rootStyle={[styles.cell, !hasTrackingStatus && styles.cellFirst]}>
                      <ShipmentTrackingNumber
                        trackingNumber={trackingLabel.tracking_no}
                        url={trackingLabel.tracking_url}
                      />
                    </TableCell>
                  </TableRow>
                )),
              )}
            </TableBody>
          </Table>
        </VStack>
      </DialogContent>
    </Dialog>
  )
}
